import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AuroraHeader, AuroraFooter } from "../components/aurora/Header";
import { ExposureGauge } from "../components/aurora/ExposureGauge";
import { BriefingCTA } from "../components/aurora/BriefingCTA";
import { api } from "../lib/api";
import { tierColor } from "../lib/countries";
import { renderNarrative, renderRecommendation, renderBolded } from "../lib/pdf-bold";

const RiskCard = ({ r }) => {
  const c = tierColor(r.tier);
  return (
    <div
      className="border border-[#A88059] p-6 flex flex-col pdf-avoid-break"
      data-testid={`risk-card-${r.category}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="font-heading-bold text-xl leading-tight text-[#190F08]">
          {r.category}
        </div>
        <div
          className="font-heading-bold text-4xl tabular-nums leading-none"
          style={{ color: c }}
        >
          {Number(r.score).toFixed(1)}
        </div>
      </div>
      <div
        className="inline-block self-start px-2 py-0.5 text-[10px] font-heading uppercase tracking-[0.22em] border mb-3"
        style={{ borderColor: c, color: c }}
      >
        {r.tier}
      </div>
      <p className="font-body text-[#190F08] text-base leading-relaxed">
        {renderNarrative(r.narrative)}
      </p>
    </div>
  );
};

export default function FullResult() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .getResult(token)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <AuroraHeader />
        <div className="flex-1 flex items-center justify-center label-eyebrow text-[#6B4D2B]">
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <AuroraHeader />
        <main className="flex-1 max-w-2xl mx-auto px-6 py-20 text-center" data-testid="error-page">
          <div className="label-eyebrow text-[#6B4D2B] mb-3">Access Denied</div>
          <h2 className="font-heading-bold text-4xl text-[#190F08] mb-4">Link Invalid</h2>
          <p className="font-body text-[#6B5E4A] leading-relaxed">
            This link is invalid or has expired. Please contact Aurora Insights at{" "}
            <a className="text-[#6B4D2B]" href="mailto:info@aurorainsights.co">
              info@aurorainsights.co
            </a>
          </p>
        </main>
        <AuroraFooter />
      </div>
    );
  }

  const f = data.fullResult;
  return (
    <div className="min-h-screen flex flex-col">
      <AuroraHeader />
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 sm:px-10 py-12">
        <div className="mb-8">
          <div className="label-eyebrow text-[#6B4D2B] mb-3">Confidential Intelligence Brief</div>
          <h2
            className="font-heading-bold text-4xl sm:text-5xl text-[#190F08] mb-2"
            data-testid="full-company"
          >
            {data.companyName}
          </h2>
          <div className="font-body italic text-[#6B5E4A]">
            Geopolitical Exposure Assessment ·{" "}
            {data.generatedAt
              ? new Date(data.generatedAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              : ""}
          </div>
        </div>

        <div className="bg-[#FAF5EE] p-2">
          {/* Human-review disclosure banner — inside printRef so it appears in the PDF too */}
          <div
            className="border-l-2 border-[#A88059] bg-[#A88059]/10 px-5 py-4 mb-6 pdf-avoid-break"
            data-testid="disclosure-full"
          >
            <p className="font-body text-[#190F08] text-sm sm:text-base leading-relaxed">
              This assessment has been rigorously reviewed and finalised by a senior Aurora
              Insights analyst.
            </p>
          </div>

          {/* Score & Rationale */}
          <div className="grid md:grid-cols-[auto_1fr] gap-10 items-center mb-6">
            <div className="flex justify-center md:justify-start">
              <ExposureGauge score={f.overallScore} tier={f.tier} size={260} />
            </div>
            <div>
              <div className="label-eyebrow text-[#6B5E4A] mb-3">Overall Exposure</div>
              <p className="font-heading text-xl text-[#190F08] leading-relaxed mb-4">
                {f.tierRationale}
              </p>
              <div
                className="inline-block px-4 py-1 border font-heading text-xs tracking-[0.22em] uppercase"
                style={{ borderColor: tierColor(f.tier), color: tierColor(f.tier) }}
              >
                {f.tier} Exposure
              </div>
            </div>
          </div>
          {/* Top 3 Risk Drivers — light box matching Aurora Insights' Recommendation styling */}
          {(f.topRiskDrivers || []).length > 0 && (
            <section
              className="mb-6 p-7 sm:p-8 border-2 border-[#A88059] bg-[#A88059]/5 pdf-avoid-break"
              data-testid="top-risk-drivers"
            >
              <div className="font-heading-bold text-2xl text-[#A88059] mb-5">
                Top 3 Risk Drivers
              </div>
              <div className="space-y-3">
                {f.topRiskDrivers.map((d) => (
                  <div key={d.rank} className="flex items-start gap-4">
                    <div className="font-heading-bold text-2xl tabular-nums shrink-0 w-12 text-[#A88059]">
                      0{d.rank}
                    </div>
                    <div className="font-body text-lg leading-snug text-[#190F08]">
                      {renderBolded(d.riskDriver)}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Executive Summary — paragraphs flow naturally, no avoid-break wrapper */}
          <section className="mb-8">
            <h3 className="font-heading-bold text-3xl text-[#A88059] mb-4">Executive Summary</h3>
            <p className="font-body text-lg text-[#190F08] leading-relaxed mb-4">
              {renderBolded(f.executiveSummary?.para1)}
            </p>
            <p className="font-body text-lg text-[#190F08] leading-relaxed">
              {renderBolded(f.executiveSummary?.para2)}
            </p>
          </section>
          {/* Data-Gap Callout — auto-generated when compulsory intake fields are missing */}
          {f.dataGapNote && (
            <section
              className="mb-8 p-6 sm:p-7 border-2 border-[#A88059] bg-[#A88059]/10 pdf-avoid-break"
              data-testid="data-gap-callout"
            >
              <div className="label-eyebrow text-[#6B4D2B] mb-3">
                Note: Critical Intake Data Gap Identified
              </div>
              <p className="font-body text-base text-[#190F08] leading-relaxed">
                {f.dataGapNote}
              </p>
            </section>
          )}
          {/* Risk Categories */}
          <section className="mb-8">
            <h3 className="font-heading text-3xl text-[#A88059] mb-4">Risk Category Dashboard</h3>
            <div className="grid md:grid-cols-2 gap-6 pdf-risk-grid" data-testid="risk-grid">
              {(f.riskScores || []).map((r) => (
                <RiskCard key={r.category} r={r} />
              ))}
            </div>
          </section>
          {/* What to Watch */}
          <section className="mb-8">
            <h3 className="font-heading-bold text-3xl text-[#A88059] mb-4">What to Watch</h3>
            <div className="space-y-4 pdf-watch-list" data-testid="watch-list">
              {(f.whatToWatch || []).map((w, i) => (
                <div
                  key={`${i}-${(w.development || "").slice(0, 40)}`}
                  className="border-l-2 border-[#A88059] pl-6 pdf-avoid-break"
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div
                      className="font-body font-bold text-xl leading-snug text-[#190F08]"
                      data-testid={`watch-headline-${i}`}
                    >
                      {renderBolded(w.development)}
                    </div>
                    {w.riskAltitude && (
                      <span
                        className="shrink-0 inline-block px-2 py-0.5 text-[10px] font-heading tracking-[0.14em] border whitespace-nowrap"
                        style={{ color: "#C4B49A", borderColor: "#C4B49A" }}
                        data-testid={`watch-altitude-${i}`}
                      >
                        {w.riskAltitude}
                      </span>
                    )}
                  </div>
                  <div className="font-body text-[#190F08] text-base leading-relaxed">{renderBolded(w.relevance)}</div>
                </div>
              ))}
            </div>
          </section>
          {/* Aurora Insights' Recommendation — timeline groups */}
          <section
            className="border-2 border-[#A88059] p-7 sm:p-8 mb-8 bg-[#A88059]/5"
            data-testid="recommendation"
          >
            <h3 className="font-heading-bold text-3xl sm:text-4xl text-[#A88059] mb-6">
              Aurora Insights&apos; Recommendation
            </h3>
            {(() => {
              const ar = f.auroraRecommendation || {};
              const groups = [
                { label: "Immediate", items: ar.immediate || [] },
                { label: "3–6 Months", items: ar.threeToSixMonths || [] },
                { label: "Long Term", items: ar.longTerm || [] },
              ];
              // Legacy fallback: if auroraRecommendation is a string, show as a paragraph
              if (typeof f.auroraRecommendation === "string") {
                return (
                  <p className="font-body text-lg text-[#190F08] leading-relaxed">
                    {renderBolded(f.auroraRecommendation)}
                  </p>
                );
              }
              return (
                <div className="space-y-7">
                  {groups.map((g) =>
                    g.items.length === 0 ? null : (
                      <div key={g.label} className="pdf-avoid-break">
                        <div className="font-heading-bold text-xl mb-3 text-[#A88059]">
                          {g.label}
                        </div>
                        <ul className="list-disc pl-5 space-y-2 font-body text-base text-[#190F08]">
                          {g.items.map((it, i) => (
                            <li key={`${g.label}-${i}`}>{renderRecommendation(it)}</li>
                          ))}
                        </ul>
                      </div>
                    )
                  )}
                </div>
              );
            })()}
          </section>
        </div>

        {/* CTA */}
        <BriefingCTA testId="full-briefing-cta" subject={`Schedule a briefing — ${data.companyName || "Aurora Insights"}`} />
      </main>
      <AuroraFooter />
    </div>
  );
}
